import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, RefreshCw, Target, TrendingUp, Heart, Calendar } from "lucide-react";

export default function LGSCoachPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/coach/report");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5 ela-fade-in" data-testid="lgs-coach-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">AI LGS Koçu</h1>
          <p className="text-sm text-slate-500 mt-1">Veriye dayalı kişisel öneriler · Claude Sonnet 4.5</p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading}
          data-testid="coach-refresh-btn" className="rounded-xl">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Raporu Yenile</span>
        </Button>
      </div>

      {!data ? (
        <Card className="p-10 rounded-2xl border-slate-200 text-center">
          <Sparkles className="w-10 h-10 text-[#FF6B6B] mx-auto mb-2" />
          <p className="text-slate-500 text-sm">Rapor hazırlanıyor…</p>
        </Card>
      ) : (
        <>
          {/* Üst kartlar */}
          <div className="grid md:grid-cols-3 gap-3">
            <MiniStat label="LGS'ye Kalan" value={`${data.summary?.days_until_lgs ?? 0} gün`} icon={Calendar} />
            <MiniStat label="Doğruluk" value={`%${data.summary?.accuracy ?? 0}`} icon={Target} />
            <MiniStat label="Bu Hafta" value={`${data.summary?.week_questions ?? 0} soru`} icon={TrendingUp} />
          </div>

          {/* Günlük öneri + motivasyon */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 rounded-2xl border-slate-200 bg-rose-50/30">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#FF6B6B]" />
                <h3 className="font-heading font-semibold text-slate-900">Bugün Yap</h3>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.gunluk_oneri || data._raw}</p>
            </Card>
            <Card className="p-5 rounded-2xl border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-pink-500" />
                <h3 className="font-heading font-semibold text-slate-900">Motivasyon</h3>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.motivasyon_mesaji}</p>
            </Card>
          </div>

          {/* Haftalık değerlendirme + tahmin */}
          <Card className="p-5 rounded-2xl border-slate-200">
            <h3 className="font-heading font-semibold mb-2">Haftalık Değerlendirme</h3>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.haftalik_degerlendirme}</p>
          </Card>

          <Card className="p-5 rounded-2xl border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              <h3 className="font-heading font-semibold">Tahmini Başarı</h3>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.tahmini_basari}</p>
          </Card>

          {data.odak_konular?.length > 0 && (
            <Card className="p-5 rounded-2xl border-slate-200">
              <h3 className="font-heading font-semibold mb-3">Odaklanman Gereken Konular</h3>
              <ul className="space-y-2">
                {data.odak_konular.map((k, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Target className="w-4 h-4 text-[#FF6B6B] mt-0.5" /> {k}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center mb-2">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
